import React, { useRef, useState } from 'react';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H1 } from '@osmosis/tailwind-ui/components/Text';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

import { FormattedMessage } from 'react-intl';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';

import LanguageTag from '../../LanguageTag';
import useClickOutside from '../../../hooks/useClickOutside';
import { usePagesContext } from '../../../context/PagesProvider';
import { StorePageWithUrl } from '@osmosis/shared/products';
import { DEFAULT_LIBRARY_URL } from '../../../constants/library';
import { useContentSize } from '@osmosis/nextjs-components/context';

type Props = {
  currentProduct?: StorePageWithUrl;
  products: StorePageWithUrl[];
};

/**
 * ProductDropdown.
 */
const ProductDropdown = ({ currentProduct, products }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { push } = usePagesContext();

  useClickOutside(containerRef, () => {
    setIsOpen(false);
  });

  const handleProductClick = (product: StorePageWithUrl) => {
    push(`${DEFAULT_LIBRARY_URL}/${product.url}`);
    setIsOpen(false);
  };

  const { getClassValue } = useContentSize();

  const dropdownBoxResponsive = getClassValue({
    defaultClasses: 'lg:absolute relative w-[90%] lg:w-[30%]',
    breakpoints: { lg: 'absolute w-[30%]', base: 'relative w-[90%]' },
  });

  const buttonResponsive = getClassValue({
    defaultClasses: 'my-2 px-2 lg:my-8',
    breakpoints: { lg: 'my-8', base: 'my-2 px-3' },
  });

  return (
    <Box cn="grid h-auto" ref={containerRef}>
      <Button
        data-testid="library-product-dropdown"
        cn={cn(
          'm-2 p-2 px-8 justify-between items-center rounded-lg hover:bg-slate-100 active:bg-slate-200 cursor-pointer',
          buttonResponsive,
        )}
        onClick={() => setIsOpen(!isOpen)}
        variant={'transparent'}
        aria-expanded={isOpen}
      >
        <H1 data-testid="library-product-dropdown-title-container">
          {currentProduct?.storeName}
        </H1>
        <Icon icon={isOpen ? faChevronUp : faChevronDown} />
      </Button>

      {isOpen && (
        <Box
          cn={`ml-2 p-8 ${bg0} left-0 top-20 z-10 shadow-lg rounded-lg ${dropdownBoxResponsive}`}
          data-testid="dropdown-box"
        >
          <H2 cn="mb-2">
            <FormattedMessage
              id="library.sidebar.product.libraries.dropdown.header"
              defaultMessage="Product Libraries"
            />
          </H2>
          {products.map((product) => (
            // eslint-disable-next-line @nx/workspace/a11y/require-keyboard-handlers
            <Flex
              cn="pl-2 py-1 items-center cursor-pointer rounded-lg w-[90%] hover:bg-slate-100 transition-all"
              key={product.key}
              data-testid="dropdown-item"
              onClick={() => handleProductClick(product)}
            >
              <Box color={'primary'}>
                {product.key === currentProduct?.key && <Icon icon={faCheck} />}
              </Box>
              <Flex cn="justify-between flex-1">
                <P
                  cn={`p-2 flex-1 text-sm ${
                    product.key === currentProduct?.key
                      ? 'text-blue-600'
                      : 'text-slate-500'
                  } hover:text-blue-600 transition-all`}
                >
                  {product.storeName}
                </P>
                {!product.translatedLibrary && (
                  <Box cn="pt-1.5 pr-2">
                    <LanguageTag size={12} />
                  </Box>
                )}
              </Flex>
            </Flex>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProductDropdown;
